import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getAuth } from 'firebase/auth'
import { useContext, useState } from 'react'

import ApiContext from '../../context/ApiContext'
import AuthContext from '../../context/AuthContext'
import Loader from '../Loader/Loader'
import { CsvResponse, columns } from './ConversationsTable/columns'
import { DataTable } from './ConversationsTable/data-table'
import { toast } from 'sonner'
import DeleteConversationDialog from '../Modals/DeleteConversationDialog'

export const TABLE_PAGE_SIZE = 6

const ImportedCsvsTable = () => {
	const auth = getAuth()
	const apiContext = useContext(ApiContext)
	const authContext = useContext(AuthContext)
	const [conversationId, setConversationId] = useState<string | null>(null)
	const queryClient = useQueryClient()
	const [pagination, setPagination] = useState({
		pageIndex: 0,
		pageSize: TABLE_PAGE_SIZE,
	})

	const fetchCsvs = async (): Promise<CsvResponse> => {
		const idToken = await auth.currentUser?.getIdToken(true)
		const response = await fetch(
			`${apiContext.apiUrl}/companies/${authContext.company?.id}/csv?pageSize=${pagination.pageSize}&pageIndex=${pagination.pageIndex}`,
			{
				method: 'Get',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${idToken}`,
				},
			},
		)
		return response.json()
	}

	const deleteConversation = async (conversationId: string) => {
		const idToken = await auth.currentUser?.getIdToken(true)
		try {
			const response = await fetch(
				`${apiContext.apiUrl}/companies/${authContext.company?.id}/conversation/${conversationId}`,
				{
					method: 'DELETE',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${idToken}`,
					},
				},
			)
			if (!response.ok) throw new Error(await response.text())
		} catch (error) {
			throw error
		}
	}

	const { mutate } = useMutation({
		mutationFn: deleteConversation,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['csvs'], refetchType: 'active' })
		},
		onError: (error: unknown) => {
			if (error instanceof Error) {
				toast.error('Something went wrong', {
					description: error.message,
				})
			}
		},
	})

	const query = useQuery<CsvResponse>({
		queryKey: ['csvs', pagination, authContext.company?.id],
		queryFn: fetchCsvs,
		enabled: !!authContext.company?.id,
		placeholderData: keepPreviousData,
	})

	if (query.isLoading || !query.data) {
		return (
			<div className="flex justify-center">
				<div className="flex flex-col items-center gap-2">
					Loading Imported Conversations
					<Loader />
				</div>
			</div>
		)
	}

	if (query.isError) {
		return <div>Error: {query.error.message}</div>
	}

	return (
		<div className="container py-10 px-0">
			<DataTable
				columns={columns}
				data={query.data.items}
				rowCount={query.data.totalCount}
				pagination={pagination}
				setPagination={setPagination}
				deleteMutation={setConversationId}
			/>
			<DeleteConversationDialog
				isOpen={!!conversationId}
				onConfirm={() => {
					if (conversationId) {
						mutate(conversationId)
					}
				}}
				onDecline={() => setConversationId(null)}
			/>
		</div>
	)
}

export default ImportedCsvsTable
